import axios from 'axios'
import { z } from 'zod'
import { API_TIMEOUT_MS, apiBaseUrl } from '@/shared/api/baseUrl'
import { getRefreshToken, getToken, saveSession } from './tokenStorage'

/**
 * Access tokenni refresh tokeni bilan yangilash — `POST /api/auth/refresh`.
 *
 * <h2>Nima uchun bu alohida modul va alohida axios nusxasi</h2>
 *
 * ⚠️ `refreshClient` — ATAYLAB interceptorsiz, `shared/api/client.ts` dagi
 * `api` EMAS. Sabab: `api` ning 401 ishlovchisi aynan shu funksiyani
 * chaqiradi. Yangilash o'sha nusxa orqali ketsa va serverning javobi 401
 * bo'lsa — interceptor yana yangilashga urinardi, u yana 401 olardi va
 * hokazo. Bugun aynan shunday sikl (`balanceApi.ts` da zod yiqilib,
 * TanStack Query cheksiz qayta urgan) kabinetni abadiy "yuklanmoqda"
 * holatida qoldirgan edi. Sikl imkoniyatini SHARTLAR bilan emas, TUZILISH
 * bilan yopamiz: bu yo'lda interceptor umuman yo'q.
 *
 * <h2>⚠️ Uchta tuzoq va ular qanday yopilgan</h2>
 * <ol>
 *   <li><b>Token AYLANADI.</b> Har muvaffaqiyatli javob YANGI refresh
 *       tokenini beradi, eskisi o'sha zahoti sarflanadi. Saqlangan qiymat
 *       {@link saveSession} orqali ALBATTA almashtiriladi. Jonli tizimda
 *       tekshirilgan: eski qiymat bilan bitta so'rov yetadi — server uni
 *       REPLAY deb biladi va oiladagi YANGI (to'g'ri) token ham darhol 401
 *       qaytara boshlaydi.</li>
 *   <li><b>Bir vaqtda ketgan so'rovlar.</b> Boshqaruv paneli bir necha
 *       so'rovni parallel yuboradi; token muddati o'tganda ularning
 *       HAMMASI 401 oladi. Har biri o'zicha yangilasa — birinchisi tokenni
 *       aylantiradi, qolganlari eski qiymat bilan boradi va REPLAY hosil
 *       qiladi, ya'ni to'liq chiqish. Shuning uchun yangilash BITTA
 *       navbatda: {@code inFlight} (bitta yorliq ichida) va Web Locks
 *       ({@code navigator.locks}, yorliqlararo).</li>
 *   <li><b>Cheksiz sikl.</b> Yuqoridagi "interceptorsiz nusxa" + chaqiruvchi
 *       tomonda so'rov BIR MARTA takrorlanishi ({@code client.ts} dagi
 *       {@code __xsibRetried}).</li>
 * </ol>
 */

/**
 * ⚠️ Interceptorsiz axios nusxasi — yuqoridagi izohga qarang.
 *
 * `export` sinov uchun ham kerak: `tests/refresh.mjs` bu nusxaga hisoblagich
 * interceptor qo'yib, parallel 401 larda yangilash AYNAN BIR MARTA
 * ketganini tekshiradi.
 */
export const refreshClient = axios.create({
  baseURL: apiBaseUrl,
  headers: { 'Content-Type': 'application/json' },
  timeout: API_TIMEOUT_MS,
})

/** `AuthResource.refresh` javobi — kirish javobi bilan bir xil shakl. */
const rotatedSchema = z.object({
  token: z.string(),
  expiresAt: z.string().nullish(),
  /**
   * ⚠️ `nullish` ATAYLAB. Kelmasa saqlangani O'CHIRILADI
   * ({@link saveSession}) va sessiya 15 daqiqadan keyin oddiygina tugaydi.
   * Muqobil — eskisini saqlab qolish — keyingi yangilashda REPLAY hosil
   * qilardi, ya'ni butun oilani o'ldirardi.
   */
  refreshToken: z.string().nullish(),
})

export type RefreshOutcome =
  /** Yangi access token olindi — so'rovni takrorlash mumkin */
  | { status: 'refreshed'; token: string }
  /**
   * Server ANIQ rad etdi (401/4xx) yoki yangilash uchun qiymat yo'q.
   * Sessiya tugadi — chiqarish kerak.
   */
  | { status: 'rejected' }
  /**
   * Serverga yetib borilmadi (tarmoq, timeout, 5xx). Sessiya HALI TUGAMADI:
   * chiqarish noto'g'ri bo'lardi — tarmoq bir soniyaga uzilgani uchun
   * operatorni ishidan haydash mumkin emas. Asl xato chaqiruvchiga boradi.
   */
  | { status: 'unavailable' }

/** Bitta yorliq ichidagi navbat. `null` — hozir yangilash ketmayapti. */
let inFlight: Promise<RefreshOutcome> | null = null

/**
 * Access tokenni yangilaydi. Bir vaqtda nechta chaqirilishidan qat'i
 * nazar, serverga BITTA so'rov ketadi — qolganlari o'sha natijani oladi.
 *
 * @param staleToken 401 olgan so'rov ishlatgan access token. Navbat
 *        kelguncha boshqa birov (boshqa yorliq yoki oldingi urinish)
 *        allaqachon yangilagan bo'lishi mumkin — u holda serverga umuman
 *        borilmaydi. Bu tekshiruvsiz ikkinchi yorliq keraksiz aylantirish
 *        qilardi va birinchisining tokenini o'ldirardi.
 */
export function refreshAccessToken(staleToken: string | null): Promise<RefreshOutcome> {
  if (inFlight) return inFlight

  // `finally` navbatni bo'shatadi. `run` rad etmaydi (barcha xatolarni
  // natijaga o'giradi), lekin kutilmagan holatda ham navbat qotib
  // qolmasligi kerak — aks holda sessiya boshqa hech qachon yangilanmasdi.
  const attempt = run(staleToken).finally(() => {
    inFlight = null
  })
  inFlight = attempt
  return attempt
}

async function run(staleToken: string | null): Promise<RefreshOutcome> {
  return withCrossTabLock(async () => {
    // Navbat kutilgan vaqt ichida token allaqachon almashgan bo'lishi
    // mumkin — u holda hech narsa qilmaymiz.
    const current = getToken()
    if (current !== null && current !== staleToken) {
      return { status: 'refreshed', token: current }
    }

    const refreshToken = getRefreshToken()
    if (refreshToken === null) return { status: 'rejected' }

    let body: unknown
    try {
      const response = await refreshClient.post<unknown>('/api/auth/refresh', { refreshToken })
      body = unwrapEnvelope(response.data)
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const status = error.response.status
        // 4xx — server tokenni ANIQ rad etdi (noma'lum, muddati o'tgan
        // yoki REPLAY; `AuthResource.refresh` uchalasiga bir xil javob
        // beradi). Sessiya tugadi.
        if (status >= 400 && status < 500) return { status: 'rejected' }
      }
      // ⚠️ Tarmoq xatosi yoki 5xx da "sessiya tugadi" deb XULOSA QILMAYMIZ.
      //
      // Bu yerda halol bo'lish kerak: javob yo'qolgan bo'lsa, server
      // tokenni sarflab ulgurgan-ulgurmaganini BILMAYMIZ. Ikkala yo'l ham
      // nomukammal — qayta urinish (javob yo'lda yo'qolgan bo'lsa REPLAY,
      // ya'ni barcha qurilmalarda chiqish) yoki urinmaslik (tarmoq bir
      // soniyaga uzilgan bo'lsa keraksiz qayta kirish). Tanlov: qayta
      // urinishga RUXSAT beriladi (keyingi 401 da), lekin foydalanuvchi
      // CHIQARILMAYDI. Sabab — tarmoqning qisqa uzilishi javobning
      // commit'dan keyin yo'qolishidan ancha ko'p uchraydi.
      return { status: 'unavailable' }
    }

    const parsed = rotatedSchema.safeParse(body)
    if (!parsed.success) return { status: 'rejected' }

    // ⚠️ ENG MUHIM QATOR: saqlangan refresh tokeni SHU YERDA almashadi.
    saveSession(parsed.data.token, parsed.data.expiresAt, parsed.data.refreshToken)
    return { status: 'refreshed', token: parsed.data.token }
  })
}

/**
 * Backend javobining yagona shakli (`XsibResponse`) — bu nusxada
 * interceptor yo'q, shuning uchun qo'lda ochiladi.
 */
function unwrapEnvelope(body: unknown): unknown {
  if (typeof body === 'object' && body !== null && 'success' in body && 'data' in body) {
    return (body as { data: unknown }).data
  }
  return body
}

/**
 * YORLIQLARARO navbat.
 *
 * ⚠️ Nega modul o'zgaruvchisi (`inFlight`) YETARLI EMAS: operator odatda
 * panelni bir necha yorliqda ochib qo'yadi. Har yorliqda o'z JS konteksti,
 * lekin `localStorage` BITTA. Token muddati tugaganda ikkala yorliq ham
 * 401 oladi, ikkalasi ham yangilaydi — biri aylantiradi, ikkinchisi
 * ALLAQACHON SARFLANGAN qiymat bilan boradi. Bu REPLAY, ya'ni operator
 * hamma yorliqda birdan chiqib ketadi. Ya'ni tuzatish muammoni
 * kattalashtirardi.
 *
 * `navigator.locks` (Web Locks) aynan shu uchun: qulf origin bo'yicha
 * umumiy va yorliq yopilib qolsa avtomatik bo'shaydi. Yo'q bo'lsa (eski
 * brauzer, sinov muhiti) — funksiya to'g'ridan-to'g'ri bajariladi, ya'ni
 * himoya bitta yorliq doirasida qoladi.
 */
async function withCrossTabLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  if (!locks) return fn()
  return locks.request('xsib.auth.refresh', fn)
}
